import { Box, CircularProgress, Dialog, DialogContent, FormGroup, Typography, makeStyles } from "@material-ui/core";
import React from "react";

import { MoneyAmount } from "@/api/generated/api";
import { CustomButton } from "@/components/Button";
import { CustomCheckbox } from "@/components/Checkbox";
import { CustomSelector } from "@/components/Selecter";
import { useGetMasterInterestedPropertyTypes } from "@/hooks/useGetMasterData";
import theme from "@/theme";
import { blockBackButton } from "@/utilities/backButtonGuard";

import { FINANCIAL_ASSET_UNSELECTED, MissingStartupProfileFields } from "../utils/startupProfileModalLogic";

export type StartupProfileUpdate = {
  interested_property_type_ids?: number[];
  has_investment_experience?: boolean;
  financial_asset?: MoneyAmount;
};

// 金融資産の選択肢（"未選択" は入力必須のため除外）。
const FINANCIAL_ASSET_OPTIONS = (Object.values(MoneyAmount) as MoneyAmount[])
  .filter((value) => value !== MoneyAmount.未選択)
  .map((value) => ({ value, label: value }));

const useStyles = makeStyles(() => ({
  dialogPaper: {
    borderRadius: 16,
    backgroundColor: theme.palette.common.white,
  },
  dialogContent: {
    padding: theme.spacing(4, 3, 3),
  },
  title: {
    textAlign: "center",
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1.5,
    color: theme.palette.text.primary,
  },
  description: {
    marginTop: theme.spacing(2),
    textAlign: "center",
    fontSize: 15,
    lineHeight: 1.7,
    color: theme.palette.text.primary,
  },
  note: {
    marginTop: theme.spacing(0.5),
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  section: {
    marginTop: theme.spacing(3),
  },
  formLabel: {
    fontSize: 15,
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  requiredMark: {
    color: theme.palette.error.main,
    marginLeft: theme.spacing(0.5),
  },
  twoColumn: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    marginTop: theme.spacing(1),
    "& > *": {
      width: "calc(50% - 10px)",
      margin: "5px",
    },
  },
  errorMessage: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
    fontSize: theme.typography.caption.fontSize,
  },
  stateBox: {
    marginTop: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  stateText: {
    marginTop: theme.spacing(2),
    fontSize: 14,
    color: theme.palette.text.secondary,
  },
  reloadButton: {
    marginTop: theme.spacing(2),
  },
  submitButton: {
    marginTop: theme.spacing(3),
  },
  skipButton: {
    marginTop: theme.spacing(1.5),
  },
}));

type InterestSectionStatus = "loading" | "ready" | "unavailable";

type InterestSelectionFieldProps = {
  selectedIds: number[];
  submitting: boolean;
  onToggle: (id: number) => void;
  onStatusChange: (status: InterestSectionStatus) => void;
};

// 興味項目セクション。マスタ取得をこのコンポーネント内で行うため、
// 興味項目が未入力でない場合は描画されず、無駄なAPI取得も発生しない。
const InterestSelectionField: React.FC<InterestSelectionFieldProps> = ({
  selectedIds,
  submitting,
  onToggle,
  onStatusChange,
}) => {
  const classes = useStyles();
  const { masterInterestedPropertyTypes, loading } = useGetMasterInterestedPropertyTypes();

  // マスタ取得が一度完了したか（初回描画時のちらつき防止用）。
  const [fetchSettled, setFetchSettled] = React.useState(false);
  const prevLoading = React.useRef(false);
  React.useEffect(() => {
    if (prevLoading.current && !loading) {
      setFetchSettled(true);
    }
    prevLoading.current = loading;
  }, [loading]);

  const hasOptions = masterInterestedPropertyTypes.length > 0;
  const status: InterestSectionStatus = hasOptions
    ? "ready"
    : fetchSettled
    ? "unavailable" // 取得完了かつ0件（取得失敗・空レスポンス）
    : "loading";

  React.useEffect(() => {
    onStatusChange(status);
  }, [status, onStatusChange]);

  return (
    <Box className={classes.section}>
      <Typography className={classes.formLabel}>
        興味のある項目<span className={classes.requiredMark}>*</span>
      </Typography>
      <Typography className={classes.note}>※最低1つ以上選択してください。複数選択可</Typography>

      {status === "unavailable" ? (
        <Box className={classes.stateBox}>
          <Typography className={classes.stateText}>
            選択肢を読み込めませんでした。
            <br />
            通信環境をご確認のうえ、再読み込みしてください。
          </Typography>
          <Box className={classes.reloadButton}>
            <CustomButton type="button" customVariant="outlinedPrimary" onClick={() => window.location.reload()}>
              再読み込みする
            </CustomButton>
          </Box>
        </Box>
      ) : status === "loading" ? (
        <Box className={classes.stateBox}>
          <CircularProgress />
          <Typography className={classes.stateText}>読み込み中...</Typography>
        </Box>
      ) : (
        <FormGroup className={classes.twoColumn}>
          {masterInterestedPropertyTypes.map((propertyType) => (
            <CustomCheckbox
              key={propertyType.id}
              label={propertyType.name}
              checked={selectedIds.includes(propertyType.id)}
              onChange={() => onToggle(propertyType.id)}
              disabled={submitting}
            />
          ))}
        </FormGroup>
      )}
    </Box>
  );
};

type Props = {
  open: boolean;
  missingFields: MissingStartupProfileFields;
  submitting: boolean;
  errorMessage?: string;
  onSubmit: (update: StartupProfileUpdate) => void;
  onSkip: () => void;
};

export const StartupProfileModal: React.FC<Props> = ({
  open,
  missingFields,
  submitting,
  errorMessage,
  onSubmit,
  onSkip,
}) => {
  const classes = useStyles();

  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
  const [experience, setExperience] = React.useState<boolean | null>(null);
  const [financialAsset, setFinancialAsset] = React.useState<string>("");
  const [interestStatus, setInterestStatus] = React.useState<InterestSectionStatus>("loading");

  // モーダル表示中は Android のハードウェア戻るボタンで回避されないようブロックする。
  React.useEffect(() => {
    if (!open) return undefined;
    const release = blockBackButton();
    return release;
  }, [open]);

  const toggleId = React.useCallback((checkedId: number) => {
    setSelectedIds((prev) =>
      prev.includes(checkedId) ? prev.filter((id) => id !== checkedId) : [...prev, checkedId]
    );
  }, []);

  // 表示中の各項目が入力済みか。
  const isInterestValid = !missingFields.interest || (interestStatus === "ready" && selectedIds.length > 0);
  const isExperienceValid = !missingFields.experience || experience !== null;
  const isFinancialAssetValid =
    !missingFields.financialAsset || (financialAsset !== "" && financialAsset !== FINANCIAL_ASSET_UNSELECTED);
  const canSubmit = isInterestValid && isExperienceValid && isFinancialAssetValid && !submitting;

  // 通常は「保存するまで閉じられない」が、以下の障害時のみ当セッション限定の脱出口を出す
  // （次回起動時は未入力のため再度表示される）。
  //  - 興味項目マスタの取得が回復不能（unavailable）で保存条件を満たせない
  //  - 保存が失敗した（認証切れ等を含む。errorMessage に案内が入る）
  const showSkip = (missingFields.interest && interestStatus === "unavailable") || !!errorMessage;

  const handleSubmit = () => {
    if (!canSubmit) return;
    const update: StartupProfileUpdate = {};
    if (missingFields.interest) update.interested_property_type_ids = selectedIds;
    if (missingFields.experience && experience !== null) update.has_investment_experience = experience;
    if (missingFields.financialAsset) update.financial_asset = financialAsset as MoneyAmount;
    onSubmit(update);
  };

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{ className: classes.dialogPaper }}
    >
      <DialogContent className={classes.dialogContent}>
        <Typography className={classes.title}>
          認定プランナーとの
          <br />
          相性表示に必要な情報です
        </Typography>
        <Typography className={classes.description}>
          相性の高い認定プランナーを表示するため、未入力の項目を入力してください。
        </Typography>

        {missingFields.experience && (
          <Box className={classes.section}>
            <Typography className={classes.formLabel}>
              投資経験<span className={classes.requiredMark}>*</span>
            </Typography>
            <FormGroup className={classes.twoColumn}>
              <CustomCheckbox
                label="経験あり"
                checked={experience === true}
                onChange={() => setExperience(true)}
                disabled={submitting}
              />
              <CustomCheckbox
                label="経験なし"
                checked={experience === false}
                onChange={() => setExperience(false)}
                disabled={submitting}
              />
            </FormGroup>
          </Box>
        )}

        {missingFields.financialAsset && (
          <Box className={classes.section}>
            <Typography className={classes.formLabel}>
              金融資産<span className={classes.requiredMark}>*</span>
            </Typography>
            <CustomSelector
              value={financialAsset}
              onChange={(event) => setFinancialAsset(event.target.value as string)}
              options={FINANCIAL_ASSET_OPTIONS}
              placeHolder="選択してください"
              disabled={submitting}
            />
          </Box>
        )}

        {missingFields.interest && (
          <InterestSelectionField
            selectedIds={selectedIds}
            submitting={submitting}
            onToggle={toggleId}
            onStatusChange={setInterestStatus}
          />
        )}

        {errorMessage && (
          <Typography variant="caption" className={classes.errorMessage}>
            {errorMessage}
          </Typography>
        )}

        <Box className={classes.submitButton}>
          <CustomButton type="button" customVariant="filled" onClick={handleSubmit} disabled={!canSubmit}>
            保存する
          </CustomButton>
        </Box>

        {showSkip && (
          <Box className={classes.skipButton}>
            <CustomButton type="button" customVariant="textPrimary" onClick={onSkip} disabled={submitting}>
              後で入力する
            </CustomButton>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StartupProfileModal;
